import React, { createRef, useState, useEffect } from 'react';
import { CustomInput, DropDown, SectionLabel } from '../../../components/common';
import AccordionTile from '../Accordion';
import { ResponsibleParty } from '../clients-constants';
import {
  ProductTemplate,
  Package,
  IncentiveOrganization,
  useUpdatePackageProduct,
  useCreatePackageProduct,
  ProductTemplateKey,
  PackageProduct,
} from '../../../hooks/useClients';

type props = {
  data: any;
  default: any;
  Incentives: any;
  UpdateSaveOption: (data: boolean) => void;
};

interface RewardGiftCardProps {
  packageData: Package;
  defaultTemplate: ProductTemplate;
  setSaveDataModal: React.Dispatch<React.SetStateAction<boolean>>;
  incentives: IncentiveOrganization[];
}

const RewardGiftCard: React.FunctionComponent<RewardGiftCardProps> = ({
  packageData,
  defaultTemplate,
  setSaveDataModal,
  incentives,
}): JSX.Element => {
  const selectedPackageProduct = packageData.package_products.find(
    (item: PackageProduct) => item.product.key === ProductTemplateKey.REWARD_GIFT_CARD,
  );
  const { createPackageProduct } = useCreatePackageProduct(
    packageData.client_id!,
    selectedPackageProduct?.package_id! || packageData.id!,
    defaultTemplate.id,
  );
  const { updatePackageProduct } = useUpdatePackageProduct(packageData.client_id!, selectedPackageProduct?.id!);

  const [reward, setReward] = useState(0);
  const [rewardMilestoneValue, setRewardMilestoneValue] = useState<any>();
  const [image, setImagePreview] = useState<any>();
  const [preloadedImage, setPreloadedImage] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [rewardGiftCardTab, setRewardGiftCardTab] = useState(false);
  const fileInput: any = createRef();
  const [enableSaveOption, setEnableSaveOption] = useState(false);
  const [errorMessage, setErrorMessage] = useState<any>({});
  const [campaign, setCampaign] = useState();
  const [incentiveValue, setIncentiveValue] = useState<string>();

  const onFileChange = async (event: any) => {
    try {
      if (event.target.files && event.target.files[0]) {
        let reader = new FileReader();
        reader.onload = (e: any) => {
          setPreloadedImage(false);
          setImagePreview(e.target.result);
          setEnableSaveOption(true);
        };
        reader.readAsDataURL(event.target.files[0]);
      }
    } catch (error) {
      console.error('err--', error);
    }
  };

  const handleImageInput = (e: any) => {
    fileInput.current.click();
  };

  const Validation = () => {
    if (rewardGiftCardTab) {
      if (!reward) {
        setErrorMessage({ reward: 'Field is required' });
        return false;
      } else if (reward.toString().indexOf('.') !== -1) {
        setErrorMessage({
          reward: 'Number may not include decimals - whole numbers only',
        });
        return false;
      } else if (reward <= 0 || reward > 50) {
        setErrorMessage({ reward: 'Must be a number between 1 and 50' });
        return false;
      } else if (!rewardMilestoneValue) {
        setErrorMessage({ rewardmilestonevalue: 'Field is required' });
        return false;
      } else if (rewardMilestoneValue.toString().indexOf('.') !== -1) {
        setErrorMessage({
          rewardmilestonevalue: 'Number may not include decimals - whole numbers only',
        });
        return false;
      } else if (rewardMilestoneValue <= 0 || rewardMilestoneValue > 50) {
        setErrorMessage({ rewardmilestonevalue: 'Must be a number between 1 and 50' });
        return false;
      }
    }
    setErrorMessage({});
    return true;
  };

  const onSave = () => {
    const payload: Partial<PackageProduct> = {
      status: rewardGiftCardTab ? 'enabled' : 'disabled',
      configuration: {
        required_points: +reward,
        reward_value: +rewardMilestoneValue * 100,
        tremendous_campaign_id: campaign,
        tremendous_organization_id: incentiveValue,
        image_url: !preloadedImage ? image : image === null ? null : undefined,
      },
    };

    if (Validation()) {
      if (selectedPackageProduct) {
        updatePackageProduct.mutate(payload);
      } else {
        createPackageProduct.mutate(payload);
      }
      setEnableSaveOption(false);
    }
  };

  // Add function to get full image URL
  const getFullImageUrl = (url: string) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    const domain = process.env.REACT_APP_DOMAIN_NAME;
    return `${domain}${url}`;
  };

  useEffect(() => {
    if (packageData) {
      if (selectedPackageProduct) {
        setRewardGiftCardTab(selectedPackageProduct?.status === 'enabled' ? true : false);

        const configuration = selectedPackageProduct.configuration;
        setReward(configuration.required_points);
        setRewardMilestoneValue(configuration.reward_value / 100);
        if (configuration.image_url) {
          const fullImageUrl = getFullImageUrl(configuration.image_url);
          setImagePreview(fullImageUrl);
          setPreloadedImage(true);
          setImageError(false);
        } else {
          setImagePreview(null);
          setPreloadedImage(false);
          setImageError(false);
        }
        setIncentiveValue(configuration.tremendous_organization_id);
      } else {
        setRewardGiftCardTab(false);
        const configuration = defaultTemplate.configuration;
        setReward(configuration.required_points);
        setRewardMilestoneValue(configuration.reward_value / 100);
        if (configuration.image_url) {
          const fullImageUrl = getFullImageUrl(configuration.image_url);
          setImagePreview(fullImageUrl);
          setPreloadedImage(true);
          setImageError(false);
        } else {
          setImagePreview(null);
          setPreloadedImage(false);
          setImageError(false);
        }
      }
      preloadedImage && setEnableSaveOption(false);
    }
  }, [packageData]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setSaveDataModal(enableSaveOption);
  }, [enableSaveOption]);

  return (
    <AccordionTile
      setStatus={(data: boolean) => {
        if ((!data && Validation()) || data) {
          !enableSaveOption && setEnableSaveOption(true);
          setRewardGiftCardTab(data);
        }
      }}
      Label={'REWARD GIFT CARD'}
      buttonText={'SAVE'}
      status={rewardGiftCardTab}
      id={'rewardGiftCardTab'}
      initialstatus={true}
      disableButton={enableSaveOption}
      onButtonClick={() => {
        if (enableSaveOption) onSave();
      }}
      InnerComponent={
        <div className="my-10 flex justify-evenly gap-7">
          <div className="w-full">
            <div className="py-4">
              <CustomInput
                inputType={'number'}
                value={reward}
                setValue={(data) => {
                  !enableSaveOption && setEnableSaveOption(true);
                  setReward(data);
                }}
                Label={'Reward Milestone'}
                Error={errorMessage?.reward?.length > 0 ? true : false}
                ErrorMessage={errorMessage?.reward}
                dataTestId="reward-milestone"
              />
            </div>
            <div className="py-4">
              <label className="text-blue-dark mb-2 block text-sm font-medium leading-4">Reward Milestone Value</label>
              <input
                type={'text'}
                value={rewardMilestoneValue}
                className="text-gray-dark bg-gray-background-light w-full rounded-md px-3 py-2 text-sm font-extrabold leading-4 focus:ring-transparent"
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, '');
                  if (val !== rewardMilestoneValue.toString()) {
                    !enableSaveOption && setEnableSaveOption(true);
                  }
                  setRewardMilestoneValue(val);
                }}
                data-testid="reward-milestone-value"
              />
              {errorMessage?.rewardmilestonevalue?.length > 0 && (
                <label className="text-red mb-2 block pt-2 text-sm font-medium leading-4">
                  {' '}
                  {errorMessage?.rewardmilestonevalue}
                </label>
              )}
            </div>
            <div className="py-2">
              <DropDown
                Label={'Responsible Party'}
                value={campaign}
                setValue={(data) => {
                  !enableSaveOption && setEnableSaveOption(true);
                  setCampaign(data);
                }}
                data-testid="responsible-party"
              >
                {incentives?.map((item: IncentiveOrganization) => (
                  <option key={item.value} value={item.value}>
                    {item.text}
                  </option>
                ))}
              </DropDown>
            </div>
            <div className="py-2">
              <DropDown
                Label={' Incentive Campaign'}
                value={incentiveValue}
                setValue={(data) => {
                  !enableSaveOption && setEnableSaveOption(true);
                  setIncentiveValue(data);
                }}
                data-testid="incentive-campaign"
              >
                {ResponsibleParty.map((item: any) => (
                  <option key={item.value} value={item.value}>
                    {item.text}
                  </option>
                ))}
              </DropDown>
            </div>
          </div>
          <div className="w-full">
            <div className="py-4">
              <SectionLabel Label="Reward Graphic" />
            </div>
            <div className="flex w-full">
              <div
                className="aspect-ratio-container flex w-full cursor-pointer items-center justify-center rounded-sm bg-gray-200"
                onClick={handleImageInput}
                data-testid="add-reward-graphic-gift-card"
              >
                {image && !imageError ? (
                  <img
                    className="h-full w-full object-contain"
                    src={image}
                    alt="clientImage"
                    onError={() => {
                      setImageError(true);
                      setImagePreview(null);
                      setPreloadedImage(false);
                    }}
                  />
                ) : (
                  <div className="flex h-56 items-center justify-center">
                    <button className="flex h-6 w-6 items-center justify-center rounded-full bg-white text-3xl">
                      +
                    </button>
                    <input
                      ref={fileInput}
                      id="file-upload"
                      type="file"
                      className="hidden"
                      accept="image/*"
                      onClick={() => {
                        fileInput.current.value = null;
                      }}
                      onChange={(data) => {
                        !enableSaveOption && setEnableSaveOption(true);
                        onFileChange(data);
                      }}
                    />
                  </div>
                )}
              </div>
              <div className="pl-6">
                {image && !imageError && (
                  <button
                    className="my-2 w-full rounded-full bg-gray-300 p-2 text-center text-sm text-white"
                    onClick={() => {
                      setImagePreview(null);
                      setImageError(false);
                      !enableSaveOption && setEnableSaveOption(true);
                    }}
                    data-testid="gift-card-remove-reward-graphic"
                  >
                    REMOVE
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      }
    />
  );
};

export default RewardGiftCard;
